import React, { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCards, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-cards';
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import { CgWebsite } from "react-icons/cg";
import { BsGithub } from "react-icons/bs";
import { FaVideo } from "react-icons/fa";
import { SiArxiv } from "react-icons/si";
import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const StyledContainer = styled(Container)`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05));
  border-radius: 25px;
  padding: 50px;
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.37);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.18);
  margin-bottom: 70px;
`;

const StyledSwiper = styled(Swiper)`
  width: 100%;
  height: 550px;
`;

const StyledSlide = styled(SwiperSlide)`
  background-color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.02);
  }
`;

const ProjectImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const SlidePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 18px;
  background: linear-gradient(135deg, #2a1245 0%, #623686 55%, #8e44ad 100%);
  color: white;
  font-size: 2rem;
  font-weight: 600;
  text-align: center;
  padding: 30px;

  svg {
    font-size: 5.5rem;
    opacity: 0.9;
  }
`;

const ProjectInfo = styled(Card)`
  height: 100%;
  background-color: rgba(0, 0, 0, 0.2);
  color: white;
  border: none;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 30px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
`;

const ProjectTitle = styled(Card.Title)`
  font-size: 2.8rem;
  margin-bottom: 20px;
  color: #c770f0;
  text-align: center;
  animation: ${fadeIn} 0.5s ease;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
`;

const ProjectDescription = styled(Card.Text)`
  font-size: 1.3rem;
  margin-bottom: 30px;
  text-align: center;
  line-height: 1.6;
  animation: ${fadeIn} 0.5s ease 0.2s both;
`;

const TechStackContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin: 20px 0;
  animation: ${fadeIn} 0.5s ease 0.3s both;
`;

const TechBadge = styled.span`
  background-color: rgba(199, 112, 240, 0.2);
  color: #c770f0;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  border: 1.5px solid rgba(199, 112, 240, 0.4);
  transition: all 0.3s ease;

  &:hover {
    background-color: rgba(199, 112, 240, 0.3);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(199, 112, 240, 0.3);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 15px;
  margin-top: auto;
`;

const StyledButton = styled(Button)`
  padding: 10px 20px;
  font-size: 1rem;
  border-radius: 25px;
  transition: all 0.3s ease;
  animation: ${fadeIn} 0.5s ease 0.4s both;
  flex: 1;
  min-width: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
  font-weight: bold;
  letter-spacing: 0.5px;
  border: none;
  background-image: linear-gradient(to right, #6a11cb 0%, #2575fc 100%);
  color: white;

  &:hover {
    transform: translateY(-3px) scale(1.05);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
    background-image: linear-gradient(to right, #2575fc 0%, #6a11cb 100%);
  }

  svg {
    margin-right: 8px;
    font-size: 1.2rem;
  }

  &.btn-success {
    background-image: linear-gradient(to right, #11998e 0%, #38ef7d 100%);
    &:hover {
      background-image: linear-gradient(to right, #38ef7d 0%, #11998e 100%);
    }
  }

  &.btn-danger {
    background-image: linear-gradient(to right, #f5365c 0%, #f56036 100%);
    &:hover {
      background-image: linear-gradient(to right, #f56036 0%, #f5365c 100%);
    }
  }
`;

const FeaturedProjectsSlider = ({ projects }) => {
  const [selectedProject, setSelectedProject] = useState(projects[0]);

  return (
    <StyledContainer fluid>
      <Row className="align-items-center">
        <Col lg={8}>
          <StyledSwiper
            effect={'cards'}
            grabCursor={true}
            modules={[EffectCards, Autoplay]}
            className="mySwiper"
            onSlideChange={(swiper) => setSelectedProject(projects[swiper.realIndex])}
            loop={true}
            autoplay={{
              delay: 3000,
              disableOnInteraction: false,
            }}
          >
            {projects.map((project, index) => (
              <StyledSlide key={index}>
                {project.imgPath ? (
                  <ProjectImage src={project.imgPath} alt={project.title} style={project.paperLink ? { objectFit: "contain", padding: "12px" } : undefined} />
                ) : (
                  <SlidePlaceholder>
                    {project.icon && <project.icon />}
                    <span>{project.title}</span>
                  </SlidePlaceholder>
                )}
              </StyledSlide>
            ))}
          </StyledSwiper>
        </Col>
        <Col lg={4} className="mt-4 mt-lg-0">
          <ProjectInfo>
            <div>
              <ProjectTitle>{selectedProject.title}</ProjectTitle>
              <ProjectDescription>{selectedProject.description}</ProjectDescription>

              {/* Tech Stack Badges */}
              {selectedProject.techStack && selectedProject.techStack.length > 0 && (
                <TechStackContainer>
                  {selectedProject.techStack.map((tech, index) => (
                    <TechBadge key={index}>{tech}</TechBadge>
                  ))}
                </TechStackContainer>
              )}
            </div>
            <ButtonGroup>
              {selectedProject.paperLink && (
                <StyledButton variant="primary" href={selectedProject.paperLink} target="_blank" rel="noopener noreferrer">
                  <SiArxiv /> Paper
                </StyledButton>
              )}
              {selectedProject.ghLink && (
                <StyledButton variant="primary" href={selectedProject.ghLink} target="_blank" rel="noopener noreferrer">
                  <BsGithub /> GitHub
                </StyledButton>
              )}
              {!selectedProject.ghLink && !selectedProject.paperLink && !selectedProject.demoLink && (
                <span style={{ opacity: 0.75, fontStyle: "italic" }}>Code is private, happy to walk through it</span>
              )}
              {selectedProject.demoLink && (
                <StyledButton variant="success" href={selectedProject.demoLink} target="_blank" rel="noopener noreferrer">
                  <CgWebsite /> Demo
                </StyledButton>
              )}
              {selectedProject.loomLink && (
                <StyledButton variant="danger" href={selectedProject.loomLink} target="_blank" rel="noopener noreferrer">
                  <FaVideo /> Loom
                </StyledButton>
              )}
            </ButtonGroup>
          </ProjectInfo>
        </Col>
      </Row>
    </StyledContainer>
  );
};

export default FeaturedProjectsSlider;